import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Headphones, Send, Loader2, CheckCircle, AlertCircle, MessageCircle, User } from 'lucide-react';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

export default function PublicTicketReplyPage() {
  const { ticketId } = useParams();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const [ticket, setTicket] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTicket();
  }, [ticketId, email]);

  const fetchTicket = async () => {
    if (!ticketId || !email) {
      setError('Link non valido');
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/public/ticket/${ticketId}?email=${encodeURIComponent(email)}`);
      setTicket(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ticket non trovato o email non corrisponde');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/public/ticket/reply`, {
        ticket_id: ticketId,
        email: email,
        message: message.trim()
      });
      setSuccess(true);
      setMessage('');
      fetchTicket();
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nell\'invio della risposta');
    } finally {
      setSending(false);
    }
  };

  const statusLabels = { open: 'Aperto', in_progress: 'In Lavorazione', resolved: 'Risolto', closed: 'Chiuso' };

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <header className="bg-[#002FA7] text-white">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/10 mb-3">
              <Headphones className="w-6 h-6" />
            </div>
            <h1 className="text-2xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Rispondi al Ticket
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 -mt-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
          </div>
        ) : error && !ticket ? (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-6 text-center">
              <AlertCircle className="w-10 h-10 mx-auto mb-3 text-red-500" />
              <p className="text-red-700">{error}</p>
              <Link to="/support">
                <Button variant="outline" className="mt-4">Torna al Supporto</Button>
              </Link>
            </CardContent>
          </Card>
        ) : ticket && (
          <>
            {/* Ticket Info */}
            <Card className="border border-[#E4E4E7] shadow-sm mb-4" data-testid="reply-ticket-info">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="font-medium text-lg">{ticket.title}</h2>
                  <Badge className="bg-blue-100 text-[#002FA7] border-[#002FA7] border">
                    {statusLabels[ticket.status] || ticket.status}
                  </Badge>
                </div>
                <p className="text-sm text-[#71717A]">Ticket #{ticket.id?.slice(-8)}</p>
              </CardContent>
            </Card>

            {/* Messages */}
            <Card className="border border-[#E4E4E7] shadow-sm mb-4" data-testid="reply-messages">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  Conversazione
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {(!ticket.messages || ticket.messages.length === 0) ? (
                  <p className="text-sm text-[#71717A] text-center py-4">Nessun messaggio ancora</p>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {ticket.messages.map((msg, idx) => (
                      <div key={idx} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] rounded-lg p-3 ${
                          msg.sender === 'user' 
                            ? 'bg-[#002FA7] text-white' 
                            : 'bg-white border border-[#E4E4E7]'
                        }`}>
                          <p className={`text-xs font-medium mb-1 ${msg.sender === 'user' ? 'text-blue-100' : 'text-[#71717A]'}`}>
                            {msg.sender_name}
                          </p>
                          <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                          <p className={`text-xs mt-1 ${msg.sender === 'user' ? 'text-blue-200' : 'text-[#A1A1AA]'}`}>
                            {new Date(msg.timestamp).toLocaleString('it-IT')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Reply Form */}
            {success && (
              <Card className="border-green-200 bg-green-50 mb-4">
                <CardContent className="p-4 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">Risposta inviata!</p>
                    <p className="text-sm text-green-600">Il tecnico vedrà il tuo messaggio.</p>
                  </div>
                  <Button variant="outline" size="sm" className="ml-auto border-green-300 text-green-700" onClick={() => setSuccess(false)}>
                    Scrivi ancora
                  </Button>
                </CardContent>
              </Card>
            )}

            {!success && ticket.status !== 'closed' && (
              <Card className="border border-[#E4E4E7] shadow-sm" data-testid="reply-form">
                <CardContent className="p-4">
                  <form onSubmit={handleSubmit}>
                    {error && (
                      <div className="mb-3 p-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded">
                        {error}
                      </div>
                    )}
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Scrivi la tua risposta..."
                      rows={4}
                      required
                      className="border-[#E4E4E7] mb-3"
                      data-testid="reply-textarea"
                    />
                    <Button
                      type="submit"
                      disabled={sending || !message.trim()}
                      className="w-full bg-[#002FA7] hover:bg-[#002FA7]/90 text-white"
                      data-testid="reply-submit-btn"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                      Invia Risposta
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {ticket.status === 'closed' && (
              <Card className="border border-[#E4E4E7]">
                <CardContent className="p-4 text-center text-[#71717A]">
                  Questo ticket è stato chiuso. Per nuove richieste, <Link to="/support" className="text-[#002FA7] hover:underline">apri un nuovo ticket</Link>.
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
}
